import { Button } from "@/components/ui/Button";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/Drawer";
import { Label } from "@/components/ui/Label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { Separator } from "@/components/ui/Separator";
import { useSearch } from "@/contexts/SearchContext";
import {
  PropertyBathroomType,
  PropertyBedroomType,
} from "@/enums/property.enum";
import clsx from "clsx";
import { startCase } from "lodash";
import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import { RefObject, useMemo, useRef } from "react";
import { useOnClickOutside, useToggle } from "usehooks-ts";
import { PropertyFilterProps } from "./type";

export const PropertyBedRoomAndBathroomFilter = ({
  variant,
  title,
}: PropertyFilterProps) => {
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const drawerContentRef = useRef<HTMLDivElement>(null);

  const t = useTranslations("shared");
  const [open, toggleOpen, setOpen] = useToggle();

  useOnClickOutside(popoverContentRef as RefObject<HTMLElement>, () =>
    setOpen(false)
  );

  useOnClickOutside(drawerContentRef as RefObject<HTMLElement>, (event) => {
    const target = event.target as HTMLElement;
    const isSelectContent = target.closest('[role="listbox"]');

    if (!isSelectContent) {
      setOpen(false);
    }
  });

  const { bedrooms, bathrooms, toggleBathroom, toggleBedroom, updateState } =
    useSearch();

  const handleClear = () => {
    updateState({ bedrooms: null, bathrooms: null });
  };

  const mappedBedrooms = (bedrooms: Array<PropertyBedroomType>) => {
    return `${bedrooms
      .sort()
      .map((value) => t(`filters.bed-and-baths.options.${value}`))
      .map(startCase)
      .join(",")} ${t("words.beds")}`;
  };

  const mappedBathrooms = (bathrooms: Array<PropertyBathroomType>) => {
    return `${bathrooms
      .sort()
      .map((value) => t(`filters.bed-and-baths.options.${value}`))
      .join(",")} ${t("words.baths")}`;
  };

  const bedAndBedsTitle = useMemo(() => {
    if (!bedrooms && !bathrooms) {
      return t("words.bed-and-baths");
    }

    if (bedrooms && bathrooms) {
      return `${mappedBedrooms(bedrooms)} & ${mappedBathrooms(bathrooms)}`;
    }

    if (bedrooms) {
      return mappedBedrooms(bedrooms);
    }

    if (bathrooms) {
      return mappedBathrooms(bathrooms);
    }
  }, [bedrooms, bathrooms]);

  const shouldBeActive =
    ((bedrooms && bedrooms.length > 0) ||
      (bathrooms && bathrooms.length > 0)) ??
    false;

  const Content = () => {
    return (
      <div
        className={clsx("w-full flex flex-col gap-2 sm:gap-4", {
          "py-2 sm:pt-3": title,
        })}
      >
        <div className="flex flex-col gap-2">
          <Label>{t("words.bedrooms")}</Label>
          <div className="flex gap-1.5 flex-wrap">
            {Object.values(PropertyBedroomType).map((value) => (
              <Button
                key={value}
                onClick={() => toggleBedroom(value)}
                className="capitalize"
                variant={bedrooms?.includes(value) ? "active" : "outline"}
              >
                {t(`filters.bed-and-baths.options.${value}`)}
              </Button>
            ))}
          </div>
        </div>

        <Separator />

        <div className="flex flex-col gap-2">
          <Label>{t("words.bathrooms")}</Label>
          <div className="flex gap-1.5 flex-wrap">
            {Object.values(PropertyBathroomType).map((value) => (
              <Button
                onClick={() => toggleBathroom(value)}
                key={value}
                variant={bathrooms?.includes(value) ? "active" : "outline"}
                className="capitalize"
              >
                {t(`filters.bed-and-baths.options.${value}`)}
              </Button>
            ))}
          </div>
        </div>

        {!title && (
          <>
            <Separator />

            <div className="space-x-2 flex items-end">
              <Button
                variant={"tertiary"}
                onClick={handleClear}
                className="w-full"
              >
                {t("filters.cta.clear")}
              </Button>
              <Button
                variant={"secondary"}
                onClick={() => setOpen(false)}
                className="w-full"
              >
                {t("filters.cta.done")}
              </Button>
            </div>
          </>
        )}
      </div>
    );
  };

  switch (variant) {
    case "plain":
      return <Content />;
    case "popover":
      return (
        <Popover open={open}>
          <PopoverTrigger asChild className="h-full">
            <Button
              variant={shouldBeActive ? "active" : "outline"}
              onClick={toggleOpen}
              className="min-w-fit"
            >
              {bedAndBedsTitle}
              <ChevronDown className="size-4.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent ref={popoverContentRef} className="w-full">
            <Content />
          </PopoverContent>
        </Popover>
      );
    case "drawer":
      return (
        <Drawer open={open} key={"amenities-drawer"}>
          <DrawerTrigger asChild className="h-full text-base">
            <Button
              variant={shouldBeActive ? "active" : "outline"}
              onClick={toggleOpen}
            >
              <span className="max-w-32 truncate">{bedAndBedsTitle}</span>
              <ChevronDown className="size-4.5" />
            </Button>
          </DrawerTrigger>
          <DrawerContent
            className="w-full flex flex-col gap-4 p-2"
            ref={drawerContentRef}
          >
            <DrawerTitle>{t("filters.bed-and-baths.title")}</DrawerTitle>
            <Content />
          </DrawerContent>
        </Drawer>
      );
  }
};
